import { cache, useState } from 'react';
import useBackend from '@/backend';
import { UserVisibilityEnum, useSettingsStoreActions } from '@/store';
import { redirect, useRouter } from 'next/navigation';

interface Settings {
	maintenance: boolean;
	geo_blocked: boolean;
}

interface FetchResponse<T> {
	success: boolean;
	data: T | null;
	error: string | null;
}

/* --------------------------------- OPTIONS -------------------------------- */

const headers = {
	'Content-Type': 'application/json',
	Authorization: null,
};

/* -------------------------------------------------------------------------- */

export const useGetInitialAppSettingsAndGeo = () => {
	const router = useRouter();
	const { setUserVisibility } = useSettingsStoreActions();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { url, url_V3, getAccessToken } = useBackend();

	const getInitialAppSettingsAndGeo = cache(async (): Promise<Settings> => {
		setLoading(true);
		setError(null);

		const fetchSettings = async (): Promise<{ maintenance: boolean }> => {
			try {
				const response = await fetch(`${url}/common/get-settings`, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				});

				const data = await response.json();

				if (!response.ok) return { maintenance: true };

				const maintenance = data?.data?.maintenance === 'true';
				return { maintenance };
			} catch {
				return { maintenance: true };
			}
		};

		const fetchGeo = async (): Promise<{ geo_blocked: boolean }> => {
			try {
				const token = await getAccessToken({ v: 3 });
				if (token) {
					headers.Authorization = token;
				}
				const response = await fetch(`${url_V3}/user/geo-location`, {
					method: 'POST',
					headers,
					body: JSON.stringify({
						geo: process.env.NEXT_PUBLIC_GEO_BLOCKING_TURNED_ON === 'true',
					}),
				});

				const data = await response.json();

				if (!response.ok) {
					return { geo_blocked: true };
				}

				return { geo_blocked: false };
			} catch {
				return { geo_blocked: true };
			}
		};

		try {
			const [settingsResult, geoResult] = await Promise.allSettled([
				fetchSettings(),
				fetchGeo(),
			]);

			const settings: Settings = {
				...(settingsResult.status === 'fulfilled'
					? settingsResult.value
					: { maintenance: true }),
				...(geoResult.status === 'fulfilled'
					? geoResult.value
					: { geo_blocked: true }),
			};

			if (settings.maintenance) {
				setUserVisibility(UserVisibilityEnum.MAINTENANCE);
				router.replace('/maintenance');
			} else if (settings.geo_blocked) {
				setUserVisibility(UserVisibilityEnum.GEO_BLOCKED);
				router.replace('/');
			} else {
				setUserVisibility(UserVisibilityEnum.OK);
			}

			return settings;
		} catch (e: unknown) {
			const message = e instanceof Error ? e.message : 'Unknown error';
			setError(message);
			return { maintenance: true, geo_blocked: true };
		} finally {
			setLoading(false);
		}
	});

	return {
		loading,
		error,
		getInitialAppSettingsAndGeo,
	};
};
